/**
 * Deep Equal Plugin for Riot.js
 * 
 * Provides deep equality comparison functionality to all Riot components.
 * This plugin adds a `deepEqual` method to the component instance that can
 * recursively compare objects, arrays, primitives, dates, and regular expressions.
 * 
 * Usage:
 * - Install the plugin: riot.install(deepEqualPlugin)
 * - Use in components: this.deepEqual(obj1, obj2)
 */

/**
 * Deep equality comparison function
 * @param {any} obj1 - First value to compare
 * @param {any} obj2 - Second value to compare
 * @returns {boolean} - True if values are deeply equal
 */
function deepEqual(obj1, obj2) {
    // Strict equality check (handles primitives and same reference)
    if (obj1 === obj2) return true
    
    // Check for null/undefined
    if (obj1 == null || obj2 == null) return obj1 === obj2
    
    // Check if types are different
    if (typeof obj1 !== typeof obj2) return false
    
    // Handle primitives that aren't strictly equal
    if (typeof obj1 !== 'object') return false
    
    // Handle arrays
    if (Array.isArray(obj1)) {
        if (!Array.isArray(obj2)) return false
        if (obj1.length !== obj2.length) return false
        
        for (let i = 0; i < obj1.length; i++) {
            if (!deepEqual(obj1[i], obj2[i])) return false
        }
        return true
    }
    
    // Handle array vs object mismatch
    if (Array.isArray(obj2)) return false
    
    // Handle Date objects
    if (obj1 instanceof Date && obj2 instanceof Date) {
        return obj1.getTime() === obj2.getTime()
    }
    
    // Handle RegExp objects
    if (obj1 instanceof RegExp && obj2 instanceof RegExp) {
        return obj1.toString() === obj2.toString()
    }
    
    // Handle plain objects
    const keys1 = Object.keys(obj1)
    const keys2 = Object.keys(obj2)
    
    // Check if they have the same number of properties
    if (keys1.length !== keys2.length) return false
    
    // Check if all keys and values are equal
    for (const key of keys1) {
        if (!obj2.hasOwnProperty(key)) return false
        if (!deepEqual(obj1[key], obj2[key])) return false
    }
    
    return true
}

/**
 * Riot.js plugin factory for deep equality comparison
 * @returns {Function} - Plugin function for riot.install()
 */
export function createDeepEqualPlugin() {
    return function deepEqualPlugin(component) {
        // Add deepEqual method to component instance
        component.deepEqual = deepEqual
        return component
    }
}

// Export default plugin instance
export const deepEqualPlugin = createDeepEqualPlugin()